// Converts a given URL to a File object.
export async function remoteImageToFile(url) {
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error('Failed to fetch image (invalid URL or CORS error).')
  }

  const contentType = response.headers.get('content-type')
  if (!contentType || !contentType.startsWith('image/')) {
    throw new Error('That URL does not point to an image.')
  }

  const blob = await response.blob()
  if (blob.size === 0) throw new Error('Empty image file.')
  if (blob.size > 300 * 1024 * 1024)
    throw new Error('Image too large (max 300MB).')

  if (!(await isValidImageBlob(blob))) {
    throw new Error('The file at the provided URL is not a valid image.')
  }

  const fileName = generateUniqueFileNameFromUrl(url, contentType)

  const file = new File([blob], fileName, { type: contentType })

  return file
}

// Tries to infer the filename from the URL.
// Generates a generic filename as a backup.
export function generateUniqueFileNameFromUrl(url, contentType) {
  try {
    const parts = url.split('/')
    let filename = parts[parts.length - 1] || 'remote-image'
    filename = filename.split(/[?#]/)[0]

    const dotIdx = filename.lastIndexOf('.')
    let base = filename,
      ext = ''
    if (dotIdx !== -1) {
      base = filename.substring(0, dotIdx)
      ext = filename.substring(dotIdx)
    } else if (contentType && contentType.startsWith('image/')) {
      // Use contentType for extension if not present in filename
      const match = contentType.match(/^image\/(\w+)/)
      ext = match ? '.' + match[1].replace('jpeg', 'jpg') : '.jpg'
    } else {
      ext = '.jpg'
    }

    const uniqueId = Math.random().toString(16).slice(2, 10)

    return `${base}-${uniqueId}${ext || '.jpg'}`
  } catch {
    return `remote-image-${Date.now()}.jpg`
  }
}

export function isValidImageBlob(blob) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob)
    const img = new window.Image()

    img.onload = function () {
      URL.revokeObjectURL(url)
      resolve(true)
    }
    img.onerror = function () {
      URL.revokeObjectURL(url)
      resolve(false)
    }
    img.src = url
  })
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}
